/**
 * ProviderKeyManager — inline API key management for a single provider.
 *
 * Surfaced from the Provider Health tile (not a separate section). Handles
 * add / replace / remove with its own independent save flow — does NOT
 * participate in the global Edit/Save toggle on the AI Config page, since
 * key management is a discrete, security-sensitive action that should
 * persist immediately without bundling unrelated pending edits.
 *
 * Security:
 * - The key value is never stored in React state beyond the transient input
 *   field — it's sent directly to the API on save and never read back.
 * - Only the masked `keyHint` (e.g. `sk-...a91f`) is displayed.
 * - Removal requires a confirmation step since it can break active aliases.
 *
 * Backend contract:
 *   POST   /admin/ai/providers/:provider/validate-key  — validate without saving
 *   PUT    /admin/ai/providers/:provider/key            — save/replace (with validation)
 *   DELETE /admin/ai/providers/:provider/key            — remove
 */
import { useState, type JSX } from 'react'
import { InputText } from 'primereact/inputtext'
import {
  Check,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  Plus,
  Trash2,
} from 'lucide-react'

import { api } from '../../../api/client.js'
import { useToast } from '../../system/ToastProvider.js'
import { useConfirmDialog } from '../../ui/ConfirmDialog.js'
import ResourceDrawer from '../ResourceDrawer.js'

/** Non-sensitive key metadata from `GET /admin/ai/config`. */
export interface ProviderKeyInfo {
  provider: string
  hasKey: boolean
  keyHint: string | null
  source: 'tenant' | 'platform'
}

type Mode = 'idle' | 'editing'

// Field styling inside the drawer — identical to the Guardrails / Agent drawers.
const DRAWER_INPUT_CLASS =
  'w-full rounded-sm border border-gray-300 bg-white px-3 py-2 text-xs text-gray-700 focus:border-[#3454cf] focus:outline-none focus:ring-1 focus:ring-[#3454cf]'
const DRAWER_LABEL_CLASS =
  'mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-400'

interface ProviderKeyManagerProps {
  provider: string
  providerLabel: string
  keyInfo: ProviderKeyInfo | null
  /** Whether the current user can manage keys (admin:extensions:manage). */
  canManage: boolean
  /** Called after a successful save/remove so the parent can refresh config. */
  onChanged: () => void
}

export function ProviderKeyManager({
  provider,
  providerLabel,
  keyInfo,
  canManage,
  onChanged,
}: ProviderKeyManagerProps): JSX.Element {
  const toast = useToast()
  const { confirm, Dialog } = useConfirmDialog()
  const [mode, setMode] = useState<Mode>('idle')
  const [keyValue, setKeyValue] = useState('')
  const [busy, setBusy] = useState(false)
  const [validating, setValidating] = useState(false)
  const [validationError, setValidationError] = useState<string | null>(null)
  const [forceSave, setForceSave] = useState(false)
  const [showKey, setShowKey] = useState(false)

  function handleCancel(): void {
    setMode('idle')
    setKeyValue('')
    setValidationError(null)
    setForceSave(false)
  }

  async function handleValidate(): Promise<void> {
    if (!keyValue.trim()) {
      setValidationError('API key must not be empty')
      return
    }
    setValidating(true)
    setValidationError(null)
    try {
      const result = await api.post<{ ok: boolean; error?: string; keyHint?: string; latencyMs?: number }>(
        `/admin/ai/providers/${provider}/validate-key`,
        { apiKey: keyValue },
      )
      if (result.ok) {
        setValidationError(null)
        toast.success(
          `${providerLabel} key validated`,
          `Health check passed in ${result.latencyMs ?? '?'}ms`,
        )
      } else {
        setValidationError(result.error ?? 'Validation failed')
      }
    } catch (err) {
      setValidationError(String(err))
    } finally {
      setValidating(false)
    }
  }

  async function handleSave(): Promise<void> {
    if (!keyValue.trim()) {
      setValidationError('API key must not be empty')
      return
    }
    setBusy(true)
    setValidationError(null)
    try {
      const result = await api.put<{ ok: boolean; keyHint?: string; error?: string }>(
        `/admin/ai/providers/${provider}/key`,
        { apiKey: keyValue, force: forceSave },
      )
      if (result.ok) {
        toast.success(
          `${providerLabel} key saved`,
          result.keyHint ? `Key set: ${result.keyHint}` : 'Key encrypted and stored',
        )
        handleCancel()
        onChanged()
      } else {
        // Validation failed during save — offer force option
        setValidationError(result.error ?? 'Failed to save key')
        setForceSave(true)
      }
    } catch (err) {
      setValidationError(String(err))
    } finally {
      setBusy(false)
    }
  }

  async function handleRemove(): Promise<void> {
    setBusy(true)
    try {
      await api.delete(`/admin/ai/providers/${provider}/key`)
      toast.success(`${providerLabel} key removed`, 'Router will fall back to platform default')
      handleCancel()
      onChanged()
    } catch (err) {
      toast.error('Failed to remove key', String(err))
    } finally {
      setBusy(false)
    }
  }

  // ── Shared drawer — add / replace key in a side panel ──
  const keyDrawer = (
    <ResourceDrawer
      open={mode === 'editing'}
      onClose={handleCancel}
      onSave={handleSave}
      title={`${keyInfo?.hasKey ? 'Replace' : 'Add'} ${providerLabel} API Key`}
      icon={<KeyRound className="h-4 w-4 text-white" />}
      width="standard"
      saving={busy}
      disabled={!keyValue.trim()}
      saveLabel={forceSave ? 'Save Anyway' : 'Save'}
      footerStart={
        <button
          type="button"
          onClick={() => void handleValidate()}
          disabled={validating || !keyValue.trim()}
          className="inline-flex items-center gap-1.5 rounded-sm border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
        >
          {validating ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Check className="h-3.5 w-3.5" />
          )}
          Validate
        </button>
      }
    >
      <div className="flex flex-col gap-4">
        <div>
          <label className={DRAWER_LABEL_CLASS}>API key</label>
          <div className="relative">
            <KeyRound className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
            <InputText
              type={showKey ? 'text' : 'password'}
              value={keyValue}
              onChange={(e) => setKeyValue(e.target.value)}
              placeholder="Paste API key"
              className={`${DRAWER_INPUT_CLASS} !pl-8 !pr-9`}
              autoFocus
              autoComplete="off"
            />
            <button
              type="button"
              onClick={() => setShowKey((v) => !v)}
              aria-label={showKey ? 'Hide API key' : 'Show API key'}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-sm p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
            >
              {showKey ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            </button>
          </div>
          <span className="mt-1 block text-[10px] text-gray-400">
            Stored encrypted — only a masked hint is shown after saving.
          </span>
        </div>
        {validationError && <p className="text-[11px] text-red-600">{validationError}</p>}
        {forceSave && (
          <p className="rounded-sm border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
            Validation failed. You can save anyway, but the key may not work until the provider is
            reachable.
          </p>
        )}
      </div>
    </ResourceDrawer>
  )

  // ── Read-only view (no key) ──
  if (!keyInfo?.hasKey) {
    if (!canManage) {
      return (
        <div className="mt-2 flex items-center gap-1.5 text-[11px] text-gray-400">
          <KeyRound className="h-3 w-3" />
          No API key configured
        </div>
      )
    }
    return (
      <>
        <button
          type="button"
          onClick={() => setMode('editing')}
          className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-[11px] font-medium text-gray-700 transition-all hover:border-[#3454cf]/40 hover:bg-[#3454cf]/5 hover:text-[#3454cf]"
        >
          <Plus className="h-3 w-3" />
          Add API key
        </button>
        {keyDrawer}
        <Dialog />
      </>
    )
  }

  // ── Key set — show masked hint + actions ──
  return (
    <div className="mt-2 min-w-0 space-y-2">
      {keyDrawer}
      <Dialog />
      <div className="flex flex-wrap items-center gap-2">
        <KeyRound className="h-3 w-3 text-emerald-500" />
        <code className="min-w-0 break-all font-mono text-[11px] text-gray-600">
          {keyInfo.keyHint ?? '••••••••'}
        </code>
        <span
          className={`rounded px-1.5 py-0.5 text-[9px] font-medium leading-3 ${
            keyInfo.source === 'tenant'
              ? 'bg-[#3454cf]/10 text-[#3454cf]'
              : 'bg-gray-100 text-gray-500'
          }`}
        >
          {keyInfo.source === 'tenant' ? 'Tenant' : 'Platform'}
        </span>
      </div>

      {canManage && (
        <div className="flex flex-wrap items-center gap-2">
          {mode === 'idle' && (
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => setMode('editing')}
                className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
              >
                <KeyRound className="h-3 w-3" />
                Replace
              </button>
              {keyInfo.source === 'tenant' && (
                <button
                  type="button"
                  onClick={() =>
                    confirm({
                      title: `Remove ${providerLabel} API Key`,
                      message: `Remove key? Aliases using ${providerLabel} will fall back to platform default.`,
                      confirmLabel: 'Remove',
                      cancelLabel: 'Cancel',
                      onConfirm: () => void handleRemove(),
                    })
                  }
                  className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium text-red-600 transition-colors hover:bg-red-50"
                >
                  <Trash2 className="h-3 w-3" />
                  Remove
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}


